import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { Executive } from '../api/contract'
import './ShareModal.css'

/** What the modal needs to identify the candidate being shared. */
export interface ShareTarget {
  display_name: string
  reference_code: string
  match_score: number
}

export function toShareTarget(exec: Executive): ShareTarget {
  return {
    display_name: exec.display_name,
    reference_code: exec.reference_code,
    match_score: exec.match_score,
  }
}

const ShareContext = createContext<(target: ShareTarget) => void>(() => {})

/** Any card can open the share modal without threading props through the tree. */
export const useShare = () => useContext(ShareContext)

function Modal({ target, onClose }: { target: ShareTarget; onClose: () => void }) {
  const { data: team } = useQuery({ queryKey: ['team-members'], queryFn: () => api.getTeamMembers() })
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  // preselect the first recipient, matching the locked design's checked row
  const members = useMemo(() => team?.items ?? [], [team])
  useEffect(() => {
    const first = members[0]
    if (first) setSelected((s) => (s.size === 0 ? new Set([first.id]) : s))
  }, [members])

  useEffect(() => {
    closeRef.current?.focus()
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return }
      // aria-modal is a promise to keep focus inside; honour it rather than
      // relying on the browser, which will happily tab out to the page behind
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }

    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = overflow
    }
  }, [onClose])

  const count = selected.size
  const sendLabel = count === 0 ? 'Select a recipient' : `Send to ${count} ${count === 1 ? 'person' : 'people'}`

  /**
   * Prototype-local confirmation. There is no share endpoint on DashboardApi,
   * so nothing is persisted and this deliberately says "in this prototype"
   * rather than claiming a share happened. Previously both footer buttons just
   * called onClose(), so Send was indistinguishable from Cancel.
   */
  const [sent, setSent] = useState(false)
  const recipients = members.filter((m) => selected.has(m.id)).map((m) => m.name)

  return (
    <>
      <div className="mask" onClick={onClose} />
      <div
        className="modal" role="dialog" aria-modal="true" aria-labelledby="sharetitle" ref={dialogRef}
      >
        <div className="modal-h">
          <div>
            <div className="modal-e">Communication Center</div>
            <h2 className="modal-t" id="sharetitle">Share this candidate</h2>
          </div>
          <button className="modal-x" type="button" aria-label="Close" onClick={onClose} ref={closeRef}>
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 5l10 10M15 5 5 15" />
            </svg>
          </button>
        </div>

        <div className="modal-b">
          {sent && (
            <div className="share-sent" role="status">
              <b>Ready to send to {recipients.join(', ')}.</b>
              <span>
                Not delivered — this prototype has no share endpoint yet, so nothing
                was sent or saved.
              </span>
            </div>
          )}
          <div className="share-card">
            <div className="face sm" aria-hidden="true">
              <svg width="19" height="19" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="10" cy="6.8" r="3.4" />
                <path d="M3.6 17.5c0-3.5 2.9-6.1 6.4-6.1s6.4 2.6 6.4 6.1z" />
              </svg>
            </div>
            <div>
              <div className="ex-n">{target.display_name}</div>
              <div className="ex-i">Profile ID: {target.reference_code}</div>
            </div>
            <span className="mt tnum" style={{ marginLeft: 'auto' }}>{target.match_score}% Match</span>
          </div>

          <div className="share-lbl">Send to</div>
          <div className="share-list">
            {members.map((m) => (
              <label className="who" key={m.id}>
                <input
                  type="checkbox"
                  checked={selected.has(m.id)}
                  onChange={(e) => setSelected((s) => {
                    const next = new Set(s)
                    if (e.target.checked) next.add(m.id); else next.delete(m.id)
                    return next
                  })}
                />
                <span className="oav" aria-hidden="true">{m.initials}</span>
                <span className="who-n">{m.name}<span className="who-r">{m.role}</span></span>
              </label>
            ))}
          </div>

          <div className="share-lbl">Add a note <span className="share-opt">optional</span></div>
          <textarea className="share-note" rows={3} placeholder="Why you are sharing this candidate…" />

          <p className="share-fine">
            Recipients must already be on your company account. Sharing does not release the
            executive's identity.
          </p>
        </div>

        <div className="modal-f">
          {sent ? (
            <button className="fbtn" type="button" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="obtn" type="button" onClick={onClose}>Cancel</button>
              <button
                className="fbtn" type="button" disabled={count === 0}
                style={{ opacity: count === 0 ? 0.5 : 1 }}
                onClick={() => setSent(true)}
              >
                {sendLabel}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export function ShareProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<ShareTarget | null>(null)
  const opener = useRef<HTMLElement | null>(null)

  const open = useCallback((t: ShareTarget) => {
    // remember what opened us so focus can go back there on close
    opener.current = document.activeElement as HTMLElement | null
    setTarget(t)
  }, [])

  const close = useCallback(() => {
    setTarget(null)
    opener.current?.focus()
  }, [])

  return (
    <ShareContext.Provider value={open}>
      {children}
      {target && <Modal target={target} onClose={close} />}
    </ShareContext.Provider>
  )
}
